/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue } from "recoil";
import EmailSelectorFamily, { EmailMode } from "../../atoms/EmailAtom";
import { FullScreenAtom } from "../../atoms/FullScreenAtom";
import { TestDriveBannerAtom } from "../../atoms/TestDriveBannerAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { WordsLengthAtom } from "../../atoms/WordsLengthAtom";
import { ZoomAtom } from "../../atoms/ZoomAtom";
import useClickOutside from "../../hook/useClickOutside";
import useMediaQuery from "../../hook/useMediaQuery";
import { customTooltip, Flex, notSelect } from "../../styles/general";
import { Option } from "../../types/SavedSearchTypes";
import {
  localStorageGetItem,
  localStorageSetItem,
} from "../../utils/LocalStorage";
import { PADDING_ZOOM, WIDTH_ZOOM } from "../EmailEditor";
import IconBerserkerMail from "../Icon";
import CalendarLinks from "../Modal/container/CalendarLinks";
import { useModal } from "../Modal/Modal";
import MergeFields from "./MergeFields";
import ThemePicker from "./ThemePicker";
import WordSelect from "./WordSelect";

const fullScreenWrapper = css`
  width: 100%;
`;

const normalWrapper = css`
  width: calc(100% - 440px);
`;

interface WrapperProps {
  fullScreen: boolean;
  topBar?: boolean;
  isTestDriverUser: boolean;
}

const WhenIsContactsPageWrapper = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div<WrapperProps>`
  background: #ffffff;
  border-top: 1px solid #dddddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 15px;
  position: fixed;
  bottom: ${(props) => (props.topBar ? 75 : 0)}px;
  ${(props) => (props.fullScreen ? fullScreenWrapper : normalWrapper)};
  height: 50px;

  @media screen and (max-width: 1080px) {
    width: ${(props) => (props.fullScreen ? "100%" : "calc(100% - 60px)")};
  }

  @media screen and (max-width: 650px) {
    bottom: ${(props) => (props.isTestDriverUser || !props.topBar ? 0 : 75)}px;
  }

  @media screen and (max-width: 560px) {
    width: 100%;
  }
`;

const Icon = styled.img`
  width: 25px;
  height: 25px;

  flex-shrink: 0;

  cursor: pointer;
  object-fit: none;
  ${notSelect}

  @media screen and (min-width: 600px) {
    margin-left: 13px;
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const MoreOptionPopoverContainer = styled.div`
  width: 160px;
  height: fit-content;

  background: white;

  position: absolute;
  top: -160px;
  z-index: 20;

  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;

  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  div,
  button {
    width: 100%;
  }
`;

const UniqueFont = "Helvetica";

const ZOOMS: Option[] = [
  { name: "75%", id: "1" },
  { name: "100%", id: "2" },
  { name: "125%", id: "3" },
  { name: "150%", id: "4" },
  { name: "175%", id: "5" },
  { name: "200%", id: "6" },
  { name: "225%", id: "7" },
  { name: "250%", id: "8" },
  { name: "275%", id: "9" },
  { name: "300%", id: "10" },
];

const flex = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CurrentZoom = styled.div`
  width: 62px;
  color: #555555;
  cursor: pointer;
`;
const LessZoom = styled.div<{ disabled: boolean }>`
  font-size: 13px;
  font-weight: 400;
  background: ${(props) => (props.disabled ? "#dcdcdc" : "#fff")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#7f7c7c" : "#555555")};
  width: 62px;
  height: 49px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }

  ${flex}
`;
const MoreZoom = styled.div<{ disabled: boolean }>`
  font-size: 18px;
  font-weight: 400;
  background: ${(props) => (props.disabled ? "#dcdcdc" : "#fff")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#7f7c7c" : "#555555")};
  width: 62px;
  height: 49px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }

  ${flex}
`;

const zoomSquare = css`
  height: 50px;
  padding: 17px;
  border-top: 0px !important;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }

  ${flex}
  ${notSelect}
`;

const bottomButtonsCss = css`
  width: 5em;
  padding: 0 10px;
  position: relative;
  color: #555555;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: center;
  height: 50px;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 15px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const bottomActionsCss = css`
  width: 110px;
  .merge-fields-container {
    top: -220px;
  }
`;

interface Props {
  cssStyle?: any;
  mode: EmailMode;
  editor: Editor;
}

const BottomBar = (props: Props): JSX.Element => {
  const { cssStyle, mode, editor } = props;

  const words = useRecoilValue(WordsLengthAtom);
  const [email, setEmail] = useRecoilState(EmailSelectorFamily({ mode }));
  const [selectedZoom, setSelectedZoom] = useRecoilState(ZoomAtom);
  const [fullScreen, setFullScreen] = useRecoilState(FullScreenAtom);
  const [minZoomReached, setMinZoomReached] = useState(false);
  const [maxZoomReached, setMaxZoomReached] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const modal = useModal();
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const user = useRecoilValue(UserAtom);
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");

  const router = useRouter();
  const isTestDriverUser = showTestDrive.show;

  useEffect(() => {
    if (mode !== "new-email") return;
    const drafEmailZoom = parseInt(
      localStorageGetItem("DRAFT_EMAIL_ZOOM") as string
    );
    const drafEmailFont = parseInt(
      localStorageGetItem("DRAFT_EMAIL_FONT") as string
    );
    const draftEmailFontSize = parseInt(
      localStorageGetItem("DRAFT_EMAIL_FONT_SIZE") as string
    );

    setEmail((prevActiveEmail) => ({
      ...prevActiveEmail,
      ...(draftEmailFontSize && { fontSize: draftEmailFontSize }),
      ...(drafEmailFont && { font: UniqueFont }),
    }));
    if (drafEmailZoom) setSelectedZoom(ZOOMS[drafEmailZoom]);
  }, []);

  useEffect(() => {
    if (mode === "new-email") return;
    if (mode === "campaign") return;

    const savedEmail = { ...email };
    const savedEmailBody = savedEmail.body;
    let preData = savedEmailBody.substring(
      savedEmailBody.indexOf("<pre"),
      savedEmailBody.indexOf(">")
    );
    preData = `${preData}></pre>`;

    let preDataEl = document.createElement("pre");
    preDataEl.innerHTML = preData;

    preDataEl = preDataEl.firstChild as HTMLPreElement;

    const newFontFamily = preDataEl.style?.fontFamily;
    const newFontSize = preDataEl.style?.fontSize;
    if (newFontSize)
      // HERE!
      // @ts-ignore
      savedEmail.fontSize = parseInt(newFontSize.replace("px", ""));
    if (newFontFamily) savedEmail.font = newFontFamily;
    setEmail({ ...savedEmail });
  }, []);

  useEffect(() => {
    if (selectedZoom === ZOOMS[0]) setMinZoomReached(true);
    else if (selectedZoom === ZOOMS[9]) setMaxZoomReached(true);
    else {
      setMinZoomReached(false);
      setMaxZoomReached(false);
    }
  }, [selectedZoom]);

  useEffect(() => {
    if (fullScreen) {
      const emailBodyElement: HTMLDivElement | null =
        document.querySelector("#email-body");
      if (emailBodyElement) {
        const { clientWidth } = emailBodyElement;
        const maxWidth = user?.emailOptions.maxWidth;
        const nearestFullScreenWidth = Object.keys(
          WIDTH_ZOOM.fullScreen
        ).reduce((acc: string, current: string) => {
          const integerCurrentWidth =
            parseInt(current.replace("%", ""), 10) / 100;
          const integerAccWidth = parseInt(acc.replace("%", ""), 10) / 100;
          return integerCurrentWidth * maxWidth +
            parseInt(
              PADDING_ZOOM.default[current].split(" ")[1].replace("px", ""),
              10
            ) <=
            clientWidth && integerAccWidth <= integerCurrentWidth
            ? current
            : acc;
        }, "75%");

        setSelectedZoom(ZOOMS.find((z) => z.name === nearestFullScreenWidth));
      }
    }
  }, [fullScreen, user]);

  function increaseZoom() {
    const index = ZOOMS.findIndex((elem) => elem.id === selectedZoom?.id);
    if (index < 9) {
      setSelectedZoom(ZOOMS[index + 1]);
      if (mode === "new-email")
        localStorageSetItem("DRAFT_EMAIL_ZOOM", index + 1 + "");
    }
  }

  function decreaseZoom() {
    const index = ZOOMS.findIndex((elem) => elem.id === selectedZoom?.id);
    if (index > 0) {
      setSelectedZoom(ZOOMS[index - 1]);
      if (mode === "new-email")
        localStorageSetItem("DRAFT_EMAIL_ZOOM", index - 1 + "");
    }
  }

  function normalZoom() {
    setSelectedZoom(ZOOMS[1]);
    if (mode === "new-email") localStorageSetItem("DRAFT_EMAIL_ZOOM", 1 + "");
  }

  const fontOptionsRef = useClickOutside(() => {
    setShowFontPicker(false);
  });

  async function handleCopyClick() {
    // Get body element
    let html = email.body;

    html = `<p style="margin: 0px">${email.subject}</p>${html}`;

    const htmlBlob = new Blob([html], { type: "text/html" });

    let text = html;

    text = text.replace(/<style([\s\S]*?)<\/style>/gi, "");
    text = text.replace(/<script([\s\S]*?)<\/script>/gi, "");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<\/li>/gi, "\n");
    text = text.replace(/<li>/gi, "  *  ");
    text = text.replace(/<\/ul>/gi, "\n");
    text = text.replace(/<\/ol>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n");
    text = text.replace(/<\/h[1-3]>/gi, "\n");
    text = text.replace(/<br\s*[\/]?>/gi, "\n");
    text = text.replace(/<[^>]+>/gi, "");

    const textBlob = new Blob([text], { type: "text/plain" });

    // @ts-ignore
    const item = new ClipboardItem({
      "text/plain": textBlob,
      "text/html": htmlBlob,
    });

    // @ts-ignore
    await navigator.clipboard.write([item]);

    toast("Content successfully copied to the clipboard!", {
      type: "success",
    });
  }

  if (router.asPath === "/contacts") {
    return (
      <WhenIsContactsPageWrapper>
        <Flex
          css={css`
            width: ${isLargerThan600 ? "unset" : "100%"};
          `}
        >
          <div
            ref={fontOptionsRef}
            style={showFontPicker ? { backgroundColor: "#EFEFEF" } : {}}
            css={bottomButtonsCss}
          >
            {showFontPicker && <ThemePicker />}
            <div
              data-tip
              data-for="selectFont"
              onClick={() => setShowFontPicker(!showFontPicker)}
              css={bottomButtonsCss}
            >
              Display
            </div>
            {!showFontPicker && (
              <ReactTooltip
                id="selectFont"
                place="top"
                backgroundColor="#333333"
                css={customTooltip}
                effect="solid"
                globalEventOff="click"
              >
                <TooltipText>Theme</TooltipText>
              </ReactTooltip>
            )}
          </div>
          <Flex
            css={css`
              margin: ${isLargerThan600 ? "unset" : "0 auto"};
            `}
          >
            <div data-tip data-for="ZoomOut">
              <LessZoom
                onClick={() => decreaseZoom()}
                disabled={minZoomReached}
              >
                <IconBerserkerMail name="less-zoom" />
              </LessZoom>

              <ReactTooltip
                id="ZoomOut"
                place="top"
                backgroundColor="#333333"
                css={customTooltip}
                effect="solid"
              >
                <TooltipText>Zoom Out</TooltipText>
              </ReactTooltip>
            </div>

            <div data-tip data-for="ZoomZero">
              <CurrentZoom
                css={css`
                  font-size: 15px;
                  font-weight: 600;
                  ${zoomSquare}
                `}
                onClick={() => normalZoom()}
              >
                {selectedZoom.name}
              </CurrentZoom>
              <ReactTooltip
                id="ZoomZero"
                place="top"
                backgroundColor="#333333"
                css={customTooltip}
                effect="solid"
              >
                <TooltipText>Go to 100%</TooltipText>
              </ReactTooltip>
            </div>

            <div data-tip data-for="ZoomIn">
              <MoreZoom
                css={notSelect}
                onClick={() => increaseZoom()}
                disabled={maxZoomReached}
              >
                <IconBerserkerMail name="more-zoom" />
              </MoreZoom>
              <ReactTooltip
                id="ZoomIn"
                place="top"
                backgroundColor="#333333"
                css={customTooltip}
                effect="solid"
              >
                <TooltipText>Zoom In</TooltipText>
              </ReactTooltip>
            </div>

            <div
              style={{
                padding: "0 10px",
              }}
            >
              <WordSelect />
            </div>
          </Flex>
        </Flex>
      </WhenIsContactsPageWrapper>
    );
  }

  return (
    <Wrapper
      isTestDriverUser={isTestDriverUser}
      fullScreen={fullScreen}
      css={cssStyle}
      topBar={showTestDrive.show}
    >
      <Flex
        css={css`
          width: ${isLargerThan600 ? "unset" : "100%"};
        `}
      >
        <div
          ref={fontOptionsRef}
          style={showFontPicker ? { backgroundColor: "#EFEFEF" } : {}}
          css={bottomButtonsCss}
        >
          {showFontPicker && <ThemePicker />}
          <div
            data-tip
            data-for="selectFont"
            onClick={() => setShowFontPicker(!showFontPicker)}
            css={bottomButtonsCss}
          >
            Display
          </div>
          {!showFontPicker && (
            <ReactTooltip
              id="selectFont"
              place="top"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
              globalEventOff="click"
            >
              <TooltipText>Theme</TooltipText>
            </ReactTooltip>
          )}
        </div>
        <Flex
          css={css`
            margin: ${isLargerThan600 ? "unset" : "0 auto"};
          `}
        >
          <div data-tip data-for="ZoomOut">
            <LessZoom onClick={() => decreaseZoom()} disabled={minZoomReached}>
              <IconBerserkerMail name="less-zoom" />
            </LessZoom>

            <ReactTooltip
              id="ZoomOut"
              place="top"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
            >
              <TooltipText>Zoom Out</TooltipText>
            </ReactTooltip>
          </div>

          <div data-tip data-for="ZoomZero">
            <CurrentZoom
              css={css`
                font-size: 15px;
                font-weight: 600;
                ${zoomSquare}
              `}
              onClick={() => normalZoom()}
            >
              {selectedZoom.name}
            </CurrentZoom>
            <ReactTooltip
              id="ZoomZero"
              place="top"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
            >
              <TooltipText>Go to 100%</TooltipText>
            </ReactTooltip>
          </div>

          <div data-tip data-for="ZoomIn">
            <MoreZoom
              css={notSelect}
              onClick={() => increaseZoom()}
              disabled={maxZoomReached}
            >
              <IconBerserkerMail name="more-zoom" />
            </MoreZoom>
            <ReactTooltip
              id="ZoomIn"
              place="top"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
            >
              <TooltipText>Zoom In</TooltipText>
            </ReactTooltip>
          </div>
        </Flex>

        <button
          onClick={handleCopyClick}
          disabled={email.subject === "" && words === 0}
          css={css`
            ${bottomButtonsCss}
            ${bottomActionsCss}
            &:hover {
              background: #efefef;
            }
            background: transparent;
            border: none;
            &:disabled {
              opacity: 0.5;
              cursor: default;
            }

            @media (max-width: 1200px) {
              display: none;
            }
          `}
        >
          Copy All
        </button>

        <MergeFields editor={editor} setEmail={setEmail}>
          <div
            css={css`
              ${bottomButtonsCss}
              ${bottomActionsCss}
            @media (max-width: 1200px) {
                display: none;
              }
            `}
          >
            <div
              css={css`
                font-size: 15px;
                font-weight: 600;
              `}
            >
              Merge
            </div>
          </div>
        </MergeFields>

        <div
          css={css`
            ${bottomButtonsCss}
            ${bottomActionsCss}
            @media (max-width: 1200px) {
              display: none;
            }
          `}
          onClick={() => {
            modal.pushModal(<CalendarLinks editor={editor} />, "Create Event");
          }}
        >
          <div
            css={css`
              font-size: 15px;
              font-weight: 600;
            `}
          >
            Create Event
          </div>
        </div>

        {isLargerThan600 && (
          <div
            css={css`
              ${bottomButtonsCss}
              ${bottomActionsCss}
            @media (min-width: 1201px) {
                display: none;
              }
            `}
          >
            <div
              css={css`
                font-size: 15px;
                font-weight: 600;
              `}
              // onMouseDown to prevent the element from losing focus
              onMouseDown={(e) => {
                setOpenDropView(!openDropView);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              More Options
            </div>

            {openDropView && (
              <MoreOptionPopoverContainer ref={wrapperRef}>
                <button
                  onClick={handleCopyClick}
                  disabled={email.subject === "" && words === 0}
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
              &:hover {
                      background: #efefef;
                    }
                    background: transparent;
                    border: none;
                    &:disabled {
                      opacity: 0.5;
                      cursor: default;
                    }
                  `}
                >
                  Copy All
                </button>

                <MergeFields editor={editor} setEmail={setEmail}>
                  <div
                    css={css`
                      ${bottomButtonsCss}
                      ${bottomActionsCss}
                    `}
                  >
                    <div
                      css={css`
                        font-size: 15px;
                        font-weight: 600;
                      `}
                    >
                      Merge
                    </div>
                  </div>
                </MergeFields>

                <div
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
                  `}
                  onClick={() => {
                    modal.pushModal(
                      <CalendarLinks editor={editor} />,
                      "Create Event"
                    );
                  }}
                >
                  <div
                    css={css`
                      font-size: 15px;
                      font-weight: 600;
                    `}
                  >
                    Create Event
                  </div>
                </div>
              </MoreOptionPopoverContainer>
            )}
          </div>
        )}
      </Flex>
      <Flex
        css={css`
          flex-shrink: 0;
        `}
      >
        <WordSelect />
        {isLargerThan600 && (
          <Icon
            onClick={() => setFullScreen(!fullScreen)}
            src={`./icons/footer/${
              !fullScreen ? "square.svg" : "exitFullscreen.svg"
            }`}
          />
        )}
      </Flex>
    </Wrapper>
  );
};

export default BottomBar;
